/**
 * ForgeSwap Bridge Wallet Adapter
 *
 * Implements the Solana wallet adapter interface using postMessage bridge
 * for communication with the parent page's wallet injection.
 */

import {
  BaseSignerWalletAdapter,
  WalletAdapterNetwork,
  WalletConnectionError,
  WalletDisconnectionError,
  WalletNotConnectedError,
  WalletReadyState,
  WalletSignTransactionError,
} from "@solana/wallet-adapter-base";
import { PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
import { BridgeClient, WalletName } from "./bridge-client";

/**
 * Encode a transaction to base64 for bridge transport
 */
function encodeTransaction(tx: Transaction | VersionedTransaction): {
  data: string;
  versioned: boolean;
} {
  const serialized = tx.serialize({ requireAllSignatures: false });
  const data = Buffer.from(serialized).toString("base64");
  const versioned = "version" in tx;
  return { data, versioned };
}

/**
 * Decode a base64 transaction from bridge response
 */
function decodeTransaction(
  data: string,
  versioned: boolean
): Transaction | VersionedTransaction {
  const buffer = Buffer.from(data, "base64");
  if (versioned) {
    return VersionedTransaction.deserialize(buffer);
  }
  return Transaction.from(buffer);
}

export interface BridgeWalletAdapterConfig {
  walletName: WalletName;
  bridgeClient: BridgeClient;
  network?: WalletAdapterNetwork;
}

export class BridgeWalletAdapter extends BaseSignerWalletAdapter {
  name: string;
  url: string;
  icon: string;
  supportedTransactionVersions = new Set(["legacy", 0] as const);

  private _walletName: WalletName;
  private _bridgeClient: BridgeClient;
  private _publicKey: PublicKey | null = null;
  private _connecting = false;
  private _connected = false;

  constructor(config: BridgeWalletAdapterConfig) {
    super();
    this._walletName = config.walletName;
    this._bridgeClient = config.bridgeClient;

    // Set wallet-specific metadata with official icons from @solana/wallet-adapter packages
    if (config.walletName === "phantom") {
      this.name = "Phantom";
      this.url = "https://phantom.app";
      // Official Phantom icon from @solana/wallet-adapter-phantom
      this.icon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiB2aWV3Qm94PSIwIDAgMTA4IDEwOCIgZmlsbD0ibm9uZSI+CjxyZWN0IHdpZHRoPSIxMDgiIGhlaWdodD0iMTA4IiByeD0iMjYiIGZpbGw9IiNBQjlGRjIiLz4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00Ni41MjY3IDY5LjkyMjlDNDIuMDA1NCA3Ni44NTA5IDM0LjQyOTIgODUuNjE4MiAyNC4zNDggODUuNjE4MkMxOS41ODI0IDg1LjYxODIgMTUgODMuNjU2MyAxNSA3NS4xMzQyQzE1IDUzLjQzMDUgNDQuNjMyNiAxOS44MzI3IDcyLjEyNjggMTkuODMyN0M4Ny43NjggMTkuODMyNyA5NCAzMC42ODQ2IDk0IDQzLjAwNzlDOTQgNTguODI1OCA4My43MzU1IDc2LjkxMjIgNzMuNTMyMSA3Ni45MTIyQzcwLjI5MzkgNzYuOTEyMiA2OC43MDUzIDc1LjEzNDIgNjguNzA1MyA3Mi4zMTRDNjguNzA1MyA3MS41NzgzIDY4LjgyNzUgNzAuNzgxMiA2OS4wNzE5IDY5LjkyMjlDNjUuNTg5MyA3NS44Njk5IDU4Ljg2ODUgODEuMzg3OCA1Mi41NzU0IDgxLjM4NzhDNDcuOTkzIDgxLjM4NzggNDUuNjcxMyA3OC41MDYzIDQ1LjY3MTMgNzQuNDU5OEM0NS42NzEzIDcyLjk4ODQgNDUuOTc2OCA3MS40NTU2IDQ2LjUyNjcgNjkuOTIyOVpNODMuNjc2MSA0Mi41Nzk0QzgzLjY3NjEgNDYuMTcwNCA4MS41NTc1IDQ3Ljk2NTggNzkuMTg3NSA0Ny45NjU4Qzc2Ljc4MTYgNDcuOTY1OCA3NC42OTg5IDQ2LjE3MDQgNzQuNjk4OSA0Mi41Nzk0Qzc0LjY5ODkgMzguOTg4NSA3Ni43ODE2IDM3LjE5MzEgNzkuMTg3NSAzNy4xOTMxQzgxLjU1NzUgMzcuMTkzMSA4My42NzYxIDM4Ljk4ODUgODMuNjc2MSA0Mi41Nzk0Wk03MC4yMTAzIDQyLjU3OTVDNzAuMjEwMyA0Ni4xNzA0IDY4LjA5MTYgNDcuOTY1OCA2NS43MjE2IDQ3Ljk2NThDNjMuMzE1NyA0Ny45NjU4IDYxLjIzMyA0Ni4xNzA0IDYxLjIzMyA0Mi41Nzk1QzYxLjIzMyAzOC45ODg1IDYzLjMxNTcgMzcuMTkzMSA2NS43MjE2IDM3LjE5MzFDNjguMDkxNiAzNy4xOTMxIDcwLjIxMDMgMzguOTg4NSA3MC4yMTAzIDQyLjU3OTVaIiBmaWxsPSIjRkZGREY4Ii8+Cjwvc3ZnPg==";
    } else {
      this.name = "Solflare";
      this.url = "https://solflare.com";
      // Official Solflare icon from @solana/wallet-adapter-solflare
      this.icon = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJTIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MCA1MCI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOiMwMjA1MGE7c3Ryb2tlOiNmZmVmNDY7c3Ryb2tlLW1pdGVybGltaXQ6MTA7c3Ryb2tlLXdpZHRoOi41cHg7fS5jbHMtMntmaWxsOiNmZmVmNDY7fTwvc3R5bGU+PC9kZWZzPjxyZWN0IGNsYXNzPSJjbHMtMiIgeD0iMCIgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiByeD0iMTIiIHJ5PSIxMiIvPjxwYXRoIGNsYXNzPSJjbHMtMSIgZD0iTTI0LjIzLDI2LjQybDIuNDYtMi4zOCw0LjU5LDEuNWMzLjAxLDEsNC41MSwyLjg0LDQuNTEsNS40MywwLDEuOTYtLjc1LDMuMjYtMi4yNSw0LjkzbC0uNDYuNS4xNy0xLjE3Yy42Ny00LjI2LS41OC02LjA5LTQuNzItNy40M2wtNC4zLTEuMzhoMFpNMTguMDUsMTEuODVsMTIuNTIsNC4xNy0yLjcxLDIuNTktNi41MS0yLjE3Yy0yLjI1LS43NS0zLjAxLTEuOTYtMy4zLTQuNTF2LS4wOGgwWk0xNy4zLDMzLjA2bDIuODQtMi43MSw1LjM0LDEuNzVjMi44LjkyLDMuNzYsMi4xMywzLjQ2LDUuMThsLTExLjY1LTQuMjJoMFpNMTMuNzEsMjAuOTVjMC0uNzkuNDItMS41NCwxLjEzLTIuMTcuNzUsMS4wOSwyLjA1LDIuMDUsNC4wOSwyLjcxbDQuNDIsMS40Ni0yLjQ2LDIuMzgtNC4zNC0xLjQyYy0yLS42Ny0yLjg0LTEuNjctMi44NC0yLjk2TTI2LjgyLDQyLjg3YzkuMTgtNi4wOSwxNC4xMS0xMC4yMywxNC4xMS0xNS4zMiwwLTMuMzgtMi01LjI2LTYuNDMtNi43MmwtMy4zNC0xLjEzLDkuMTQtOC43Ny0xLjg0LTEuOTYtMi43MSwyLjM4LTEyLjgxLTQuMjJjLTMuOTcsMS4yOS04Ljk3LDUuMDktOC45Nyw4Ljg5LDAsLjQyLjA0LjgzLjE3LDEuMjktMy4zLDEuODgtNC42MywzLjYzLTQuNjMsNS44LDAsMi4wNSwxLjA5LDQuMDksNC41NSw1LjIybDIuNzUuOTItOS41Miw5LjE0LDEuODQsMS45NiwyLjk2LTIuNzEsMTQuNzMsNS4yMmgwWiIvPjwvc3ZnPg==";
    }
  }

  get publicKey(): PublicKey | null {
    return this._publicKey;
  }

  get connecting(): boolean {
    return this._connecting;
  }

  get connected(): boolean {
    return this._connected;
  }

  get readyState(): WalletReadyState {
    // Bridge adapter is always "loadable" - actual availability is determined at connect time
    return WalletReadyState.Loadable;
  }

  async connect(): Promise<void> {
    if (this._connected || this._connecting) return;

    this._connecting = true;

    try {
      const result = await this._bridgeClient.sendRequest<{ publicKey: string }>(
        this._walletName,
        "connect"
      );

      if (!result?.publicKey) {
        throw new WalletConnectionError("No public key returned from bridge");
      }

      this._publicKey = new PublicKey(result.publicKey);
      this._connected = true;

      console.log(
        `[ForgeSwap Bridge] Connected to ${this._walletName}:`,
        this._publicKey.toBase58()
      );

      this.emit("connect", this._publicKey);
    } catch (error) {
      this._publicKey = null;
      this._connected = false;
      throw new WalletConnectionError(
        error instanceof Error ? error.message : "Failed to connect via bridge"
      );
    } finally {
      this._connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    if (!this._connected) return;

    try {
      await this._bridgeClient.sendRequest(this._walletName, "disconnect");
    } catch (error) {
      console.warn("[ForgeSwap Bridge] Disconnect error (non-fatal):", error);
    }

    this._publicKey = null;
    this._connected = false;

    this.emit("disconnect");
    console.log(`[ForgeSwap Bridge] Disconnected from ${this._walletName}`);
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(
    transaction: T
  ): Promise<T> {
    if (!this._connected || !this._publicKey) {
      throw new WalletNotConnectedError();
    }

    try {
      const { data, versioned } = encodeTransaction(transaction);

      const result = await this._bridgeClient.sendRequest<{ signedTransaction: string }>(
        this._walletName,
        "signTransaction",
        { transaction: data, versioned }
      );

      if (!result?.signedTransaction) {
        throw new WalletSignTransactionError("No signed transaction returned from bridge");
      }

      return decodeTransaction(result.signedTransaction, versioned) as T;
    } catch (error) {
      throw new WalletSignTransactionError(
        error instanceof Error ? error.message : "Failed to sign transaction via bridge"
      );
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    transactions: T[]
  ): Promise<T[]> {
    if (!this._connected || !this._publicKey) {
      throw new WalletNotConnectedError();
    }

    try {
      const encoded = transactions.map((tx) => encodeTransaction(tx));

      const result = await this._bridgeClient.sendRequest<{ signedTransactions: string[] }>(
        this._walletName,
        "signAllTransactions",
        {
          transactions: encoded.map((e) => e.data),
          versioned: encoded.map((e) => e.versioned),
        }
      );

      if (!result?.signedTransactions || result.signedTransactions.length !== transactions.length) {
        throw new WalletSignTransactionError("Invalid signed transactions returned from bridge");
      }

      return result.signedTransactions.map((data, i) =>
        decodeTransaction(data, encoded[i].versioned)
      ) as T[];
    } catch (error) {
      throw new WalletSignTransactionError(
        error instanceof Error ? error.message : "Failed to sign transactions via bridge"
      );
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    if (!this._connected || !this._publicKey) {
      throw new WalletNotConnectedError();
    }

    try {
      const messageBase64 = Buffer.from(message).toString("base64");

      const result = await this._bridgeClient.sendRequest<{ signature: string }>(
        this._walletName,
        "signMessage",
        { message: messageBase64 }
      );

      if (!result?.signature) {
        throw new Error("No signature returned from bridge");
      }

      return Buffer.from(result.signature, "base64");
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to sign message via bridge"
      );
    }
  }
}
